import { useEffect, useCallback, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box, Card, Chip, Container, Grid, IconButton,
  Skeleton, Stack, Tooltip, Typography, alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Room, RoomEvent } from 'livekit-client';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from '../../redux/store';
import {
  fetchOrderById, updateOrderStatus, releaseOrder,
  addProductToOrder, updateOrderedProductQty, removeOrderedProduct,
} from '../../redux/features/order/orderSlice';
import { fetchProductsByOrgId } from '../../redux/features/product/productSlice';
import { selectOrder, selectOrderLoading } from '../../redux/features/order/orderSelectors';
import { selectProductsList } from '../../redux/features/product/productSelectors';
import { useAuthContext } from '../../auth/useAuthContext';
import { PATH_VOICE_AGENT } from '../../routes/paths';
import { OrderState } from '../../@enums/orderState';
import Iconify from '../../components/iconify';
import VoiceAgentOrderCart from '../../sections/voice-agent/VoiceAgentOrderCart';
import axios from '../../utils/axios';

export default function VoiceAgentOrderTakingPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const liveSteps: any[] | undefined = (location.state as any)?.liveSteps;
  const dispatch = useDispatch();
  const { user } = useAuthContext();
  const theme = useTheme();
  const order = useSelector(selectOrder);
  const isLoading = useSelector(selectOrderLoading);
  const products = useSelector(selectProductsList);
  const livekitRoomRef = useRef<Room | null>(null);
  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false);
  const [isClientSpeaking, setIsClientSpeaking] = useState(false);

  useEffect(() => {
    if (id) dispatch(fetchOrderById(id));
  }, [id, dispatch]);

  useEffect(() => {
    if (user?.subOrgId) dispatch(fetchProductsByOrgId(user.subOrgId));
  }, [user?.subOrgId, dispatch]);

  useEffect(() => {
    if (!order?.sessionId || !user?.id) return undefined;
    let room: Room;
    const connectLiveKit = async () => {
      try {
        const resp = await axios.post(
          `/api/v1/voice-agent/livekit-token?roomName=${order.sessionId}&participantName=agent-${user.id}`
        );
        const { token, url } = resp.data;
        room = new Room();
        livekitRoomRef.current = room;
        room.on(RoomEvent.ActiveSpeakersChanged, (speakers: any[]) => {
          const localIdentity = room.localParticipant?.identity;
          setIsAgentSpeaking(speakers.some((s: any) => s.identity === localIdentity));
          setIsClientSpeaking(speakers.some((s: any) => s.identity !== localIdentity));
        });
        room.on(RoomEvent.Disconnected, () => {
          livekitRoomRef.current = null;
          setIsAgentSpeaking(false);
          setIsClientSpeaking(false);
        });
        room.on(RoomEvent.TrackSubscribed, (track: any) => {
          if (track.kind === 'audio') document.body.appendChild(track.attach());
        });
        room.on(RoomEvent.TrackUnsubscribed, (track: any) => {
          track.detach();
        });
        await room.connect(url, token);
        await room.localParticipant.setMicrophoneEnabled(true);
      } catch (err) {
        console.warn('LiveKit connection failed:', err);
      }
    };
    connectLiveKit();
    return () => {
      if (livekitRoomRef.current) {
        livekitRoomRef.current.disconnect();
        livekitRoomRef.current = null;
      }
    };
  }, [order?.sessionId, user?.id]);

  const refreshOrder = useCallback(() => {
    if (id) dispatch(fetchOrderById(id));
  }, [id, dispatch]);

  const handleSendToPOS = useCallback(async () => {
    if (!order) return;
    try {
      await dispatch(updateOrderStatus({ id: order.id, orderState: OrderState.POS_ENTERED })).unwrap();
      toast.success('Commande envoyée au POS !');
      navigate(PATH_VOICE_AGENT.dashboard);
    } catch (err: any) {
      toast.error(err || "Erreur lors de l'envoi au POS.");
    }
  }, [order, dispatch, navigate]);

  const handleRelease = useCallback(async () => {
    if (!order) return;
    try {
      await dispatch(releaseOrder(order.id)).unwrap();
      toast.info("Main rendue à l'IA.");
      navigate(PATH_VOICE_AGENT.dashboard);
    } catch {
      toast.error('Erreur lors du relâchement.');
    }
  }, [order, dispatch, navigate]);

  const handleAddProduct = useCallback(async (productId: string, quantity: number) => {
    if (!order) return;
    try {
      await dispatch(addProductToOrder({ orderId: order.id, productId, quantity })).unwrap();
      refreshOrder();
    } catch {
      toast.error("Erreur lors de l'ajout du produit.");
    }
  }, [order, dispatch, refreshOrder]);

  const handleUpdateQty = useCallback(async (orderedProductId: string, quantity: number) => {
    try {
      await dispatch(updateOrderedProductQty({ id: orderedProductId, quantity })).unwrap();
      refreshOrder();
    } catch {
      toast.error('Erreur lors de la modification.');
    }
  }, [dispatch, refreshOrder]);

  const handleRemoveItem = useCallback(async (orderedProductId: string) => {
    try {
      await dispatch(removeOrderedProduct(orderedProductId)).unwrap();
      refreshOrder();
    } catch {
      toast.error('Erreur lors de la suppression.');
    }
  }, [dispatch, refreshOrder]);

  if (isLoading || !order) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="text" width={200} height={28} />
        </Stack>
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}><Skeleton variant="rounded" height={400} sx={{ borderRadius: 3 }} /></Grid>
          <Grid item xs={12} md={7}><Skeleton variant="rounded" height={400} sx={{ borderRadius: 3 }} /></Grid>
        </Grid>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`Commande #${order.id} | Agent Vocal`}</title>
      </Helmet>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Page header */}
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          <Tooltip title="Retour à la file">
            <IconButton
              onClick={() => navigate(PATH_VOICE_AGENT.dashboard)}
              size="small"
              sx={{ bgcolor: alpha(theme.palette.grey[500], 0.08), '&:hover': { bgcolor: alpha(theme.palette.grey[500], 0.14) } }}
            >
              <Iconify icon="eva:arrow-back-fill" width={18} />
            </IconButton>
          </Tooltip>

          <Typography variant="h6" fontWeight={800}>
            Commande #{order.id}
          </Typography>

          <Chip
            label="Agent actif"
            size="small"
            icon={<Iconify icon="mdi:headset" width={13} />}
            sx={{ fontWeight: 700, fontSize: '0.7rem', height: 22, bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.dark', '& .MuiChip-icon': { color: 'success.dark' } }}
          />

          {/* Speaking indicators */}
          <Box sx={{ flex: 1 }} />
          <SpeakingIndicator label="Agent" speaking={isAgentSpeaking} icon="mdi:headset" color={theme.palette.primary.main} theme={theme} />
          <SpeakingIndicator label="Client" speaking={isClientSpeaking} icon="mdi:account-voice" color={theme.palette.warning.main} theme={theme} />
        </Stack>

        <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
          {/* Transcript */}
          <Grid item xs={12} md={5}>
            <Card sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`, bgcolor: alpha(theme.palette.grey[500], 0.03) }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Iconify icon="mdi:message-text-outline" width={17} sx={{ color: 'text.secondary' }} />
                  <Typography variant="subtitle2" fontWeight={700}>Transcript</Typography>
                </Stack>
              </Box>
              <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                {(liveSteps ?? order.steps ?? []).length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Iconify icon="mdi:message-off-outline" width={32} sx={{ color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" color="text.disabled">Aucun transcript disponible</Typography>
                  </Box>
                ) : (
                  <Stack spacing={1}>
                    {(liveSteps ?? order.steps ?? []).map((step: any, i: number) => {
                      const isAI = step.speakerRole === 'AI';
                      return (
                        <Box key={i} sx={{ p: 1.25, borderRadius: 1.5, bgcolor: isAI ? alpha(theme.palette.primary.main, 0.05) : alpha(theme.palette.grey[500], 0.05), borderLeft: `2.5px solid ${isAI ? theme.palette.primary.main : theme.palette.grey[400]}` }}>
                          <Typography variant="caption" fontWeight={700} color={isAI ? 'primary.main' : 'text.secondary'} sx={{ display: 'block', mb: 0.25, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            {isAI ? 'IA' : 'Client'}
                          </Typography>
                          <Typography variant="body2" color="text.primary" lineHeight={1.5}>{step.transcript}</Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Box>
            </Card>
          </Grid>

          {/* Cart */}
          <Grid item xs={12} md={7}>
            <VoiceAgentOrderCart
              order={order}
              products={products || []}
              onSendToPOS={handleSendToPOS}
              onRelease={handleRelease}
              onAddProduct={handleAddProduct}
              onUpdateQty={handleUpdateQty}
              onRemoveItem={handleRemoveItem}
              isLoading={isLoading}
              isAgentSpeaking={isAgentSpeaking}
              isClientSpeaking={isClientSpeaking}
            />
          </Grid>
        </Grid>
      </Container>
    </>
  );
}

// ── Speaking indicator pill ──────────────────────────────────────────────────
function SpeakingIndicator({ label, speaking, icon, color, theme }: any) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={{
        px: 1.25,
        py: 0.5,
        borderRadius: 2,
        border: `1px solid ${speaking ? alpha(color, 0.35) : alpha(theme.palette.divider, 0.15)}`,
        bgcolor: speaking ? alpha(color, 0.08) : 'transparent',
        transition: 'all 0.2s ease',
      }}
    >
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          bgcolor: speaking ? color : alpha(theme.palette.text.disabled, 0.4),
          ...(speaking && {
            animation: 'speakPulse 0.8s ease-in-out infinite',
            '@keyframes speakPulse': {
              '0%,100%': { transform: 'scale(1)', opacity: 1 },
              '50%': { transform: 'scale(1.4)', opacity: 0.7 },
            },
          }),
        }}
      />
      <Iconify icon={icon} width={14} sx={{ color: speaking ? color : 'text.disabled' }} />
      <Typography variant="caption" fontWeight={600} color={speaking ? color : 'text.disabled'} sx={{ fontSize: '0.7rem' }}>
        {label}
      </Typography>
    </Stack>
  );
}
